import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuditTrailData } from "../util/getAuditTrailData";

const baseUrl = BASE_URL + "training-log";

const authHeaders = () => {
  const token =
    localStorage.rememberMe === "true"
      ? localStorage.token
      : sessionStorage.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// 📌 Create Training Log (POST)
export const createTrainingLog = createAsyncThunk(
  "trainingLog/create",
  async (trainingLogData, { rejectWithValue }) => {
    try {
      const incomingAuditTrailData = trainingLogData?.audit_trail_data;
      let audit_trail_data =
        incomingAuditTrailData ||
        (await getAuditTrailData({
          action: "CREATE_TRAINING_LOG",
          comment: "Created Training Log",
          version_content: {},
        }));

      // Ensure password + is_password_required are passed consistently (like revisions)
      const sigMethods = localStorage.signature_auth_methods || "";
      const passwordIncluded = sigMethods.includes("password");

      // Only set is_password_required if caller hasn't already set it
      if (typeof audit_trail_data.is_password_required === "undefined") {
        audit_trail_data = {
          ...audit_trail_data,
          is_password_required: !!passwordIncluded,
        };
      }

      const payload = {
        ...trainingLogData,
        audit_trail_data,
      };

      const response = await axios.post(`${baseUrl}/create`, payload);
      const data = response.data;
      if (data && typeof data === "object" && data.success === false) {
        const msg =
          typeof data.message === "string"
            ? data.message
            : "Could not create training log.";
        return rejectWithValue(msg);
      }
      return data;
    } catch (error) {
      const errData = error.response?.data;
      if (errData && typeof errData === "object" && errData.success === false) {
        const msg =
          typeof errData.message === "string"
            ? errData.message
            : "Could not create training log.";
        return rejectWithValue(msg);
      }
      return rejectWithValue(
        error.response?.data || "Error creating training log"
      );
    }
  }
);

// 📌 Update Training Log (PUT)
export const updateTrainingLog = createAsyncThunk(
  "trainingLog/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      let outgoing = { ...(data || {}) };

      // Mirror password + is_password_required behavior on update as well
      if (outgoing.audit_trail_data) {
        const sigMethods = localStorage.signature_auth_methods || "";
        const passwordIncluded = sigMethods.includes("password");

        if (
          typeof outgoing.audit_trail_data.is_password_required === "undefined"
        ) {
          outgoing.audit_trail_data = {
            ...outgoing.audit_trail_data,
            is_password_required: !!passwordIncluded,
          };
        }
      }

      const response = await axios.put(`${baseUrl}/update/${id}`, outgoing);
      const data = response.data;
      if (data && typeof data === "object" && data.success === false) {
        const msg =
          typeof data.message === "string"
            ? data.message
            : "Could not update training log.";
        return rejectWithValue(msg);
      }
      return data;
    } catch (error) {
      const errData = error.response?.data;
      if (errData && typeof errData === "object" && errData.success === false) {
        const msg =
          typeof errData.message === "string"
            ? errData.message
            : "Could not update training log.";
        return rejectWithValue(msg);
      }
      return rejectWithValue(
        error.response?.data || "Error updating training log"
      );
    }
  }
);

// 📌 Get Training Log by ID (GET)
export const getTrainingLogById = createAsyncThunk(
  "trainingLog/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching training log"
      );
    }
  }
);

// 📌 Get All Training Logs (GET)
export const getAllTrainingLogs = createAsyncThunk(
  "trainingLog/getAll",
  async (
    {
      limit = 10,
      offset = 0,
      search,
      search_type,
      sort_by,
      sort_order,
      trainer,
      trainee,
      procedure_revision_ref,
      part_ref,
      date_from,
      date_to,
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.get(`${baseUrl}/get-all`, {
        params: {
          limit,
          offset,
          search: search || undefined,
          search_type: search_type || undefined,
          sort_by: sort_by || undefined,
          sort_order: sort_order || undefined,
          trainer: trainer || undefined,
          trainee: trainee || undefined,
          procedure_revision_ref: procedure_revision_ref || undefined,
          part_ref: part_ref || undefined,
          date_from: date_from || undefined,
          date_to: date_to || undefined,
        },
        headers: authHeaders(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching training logs"
      );
    }
  }
);

// 📌 Get Training Logs for a specific Procedure Revision (GET)
// Note: This thunk is used for per-row status lookups (e.g. AddModule table).
// It intentionally does not update slice state in extraReducers (callers use unwrap()).
export const getTrainingLogsForProcedureRevision = createAsyncThunk(
  "trainingLog/getAllForProcedureRevision",
  async (
    {
      procedureRevisionId,
      limit = 100,
      offset = 0,
      search,
      search_type,
      sort_by,
      sort_order,
      trainer,
      trainee,
      part_ref,
      date_from,
      date_to,
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.get(`${baseUrl}/get-all`, {
        params: {
          procedure_revision_ref: procedureRevisionId,
          limit,
          offset,
          search: search || undefined,
          search_type: search_type || undefined,
          sort_by: sort_by || undefined,
          sort_order: sort_order || undefined,
          trainer: trainer || undefined,
          trainee: trainee || undefined,
          part_ref: part_ref || undefined,
          date_from: date_from || undefined,
          date_to: date_to || undefined,
        },
        headers: authHeaders(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching training logs"
      );
    }
  }
);

// 📌 Delete Training Log (DELETE)
export const deleteTrainingLog = createAsyncThunk(
  "trainingLog/delete",
  async (id, { rejectWithValue }) => {
    try {
      // Send auth explicitly (some deployments rely on per-request headers)
      await axios.delete(`${baseUrl}/delete/${id}`, { headers: authHeaders() });
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error deleting training log"
      );
    }
  }
);

// 🛠 Training Log Slice
const trainingLogSlice = createSlice({
  name: "trainingLog",
  initialState: {
    trainingLogs: [],
    trainingLog: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createTrainingLog.pending, (state) => {
        state.loading = true;
      })
      .addCase(createTrainingLog.fulfilled, (state, action) => {
        state.loading = false;
        state.trainingLogs.push(action.payload);
      })
      .addCase(createTrainingLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(updateTrainingLog.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTrainingLog.fulfilled, (state, action) => {
        state.loading = false;
        state.trainingLogs = state.trainingLogs.map((log) =>
          log.id === action.payload.id ? action.payload : log
        );
      })
      .addCase(updateTrainingLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getTrainingLogById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTrainingLogById.fulfilled, (state, action) => {
        state.loading = false;
        state.trainingLog = action.payload;
        state.error = null;
      })
      .addCase(getTrainingLogById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getAllTrainingLogs.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllTrainingLogs.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload ?? {};
        let raw =
          payload.results ??
          (Array.isArray(payload.data) ? payload.data : payload.data?.results);
        if (!Array.isArray(raw)) {
          raw = [];
        }
        state.trainingLogs = raw;
        state.pagination = payload.pagination ?? null;
      })
      .addCase(getAllTrainingLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(deleteTrainingLog.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteTrainingLog.fulfilled, (state, action) => {
        state.loading = false;
        const removedId = action.payload;
        state.trainingLogs = state.trainingLogs.filter(
          (log) =>
            log.training_log_id !== removedId && log.id !== removedId
        );
      })
      .addCase(deleteTrainingLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      });
  },
});

export default trainingLogSlice.reducer;
